import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  X,
  Package,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { sendPrintRequest } from "@/utils/printUtils";
import { showSuccess, showError, showWarning } from "@/utils/toastHelpers";
import { formatDate } from "@/utils/dateUtils";

export default function DispenseModal({ prescription, onClose }) {
  const queryClient = useQueryClient();
  const [selectedMedications, setSelectedMedications] = useState(new Set());

  // Fetch Xero connection status
  const { data: xeroStatus } = useQuery({
    queryKey: ["xero-status"],
    queryFn: () => apiClient.get(ENDPOINTS.BILLING.XERO_STATUS),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Get medicine IDs for in-practice medications
  const inPracticeMedicineIds =
    prescription.medications
      ?.filter((m) => m.dispenseLocation === "in_practice")
      .map((m) => m.medicineId)
      .sort()
      .join(",") || "";

  // Fetch inventory for in-practice medications
  // Include medicineIds in query key so it refetches when medications change
  const { data: inventoryItems = [], isLoading: isLoadingInventory } = useQuery(
    {
      queryKey: [
        "inventoryForPrescription",
        prescription.id,
        inPracticeMedicineIds,
      ],
      queryFn: async () => {
        const medicineIds = prescription.medications
          ?.filter((m) => m.dispenseLocation === "in_practice")
          .map((m) => m.medicineId);

        if (!medicineIds || medicineIds.length === 0) return [];

        // Fetch all inventory and filter by medicineIds (include all items to show expired status)
        const allInventory = await apiClient.get(ENDPOINTS.INVENTORY.LIST);
        return allInventory.filter(
          (item) =>
            medicineIds.includes(item.medicineId) && item.currentStock > 0
        );
      },
      enabled: !!prescription.medications,
    }
  );

  // Dispense mutation
  const dispenseMedications = useMutation({
    mutationFn: (data) =>
      apiClient.post(ENDPOINTS.PRESCRIPTIONS.DISPENSE(prescription.id), data),
    onSuccess: async (response, variables) => {
      // Invalidate queries to refetch updated data
      // Invalidate the specific prescription query for this prescription
      queryClient.invalidateQueries(["prescription", prescription.id]);
      // Also invalidate the list query in case it's visible
      queryClient.invalidateQueries(["prescriptions"]);
      // Invalidate related queries
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventoryTransactions"]);
      queryClient.invalidateQueries(["invoices"]);

      // Send print request for each dispensed medication
      let printSuccessCount = 0;
      let printFailCount = 0;

      try {
        for (const medData of variables.medications) {
          const medication = prescription.medications?.find(
            (m) => m.id === medData.medicationId
          );
          if (medication) {
            try {
              const result = await sendPrintRequest(medication, prescription);
              if (result.success === true) {
                printSuccessCount++;
              } else {
                printFailCount++;
              }
            } catch (err) {
              printFailCount++;
            }
          }
        }
      } catch (printError) {
        // Silent catch - don't block the flow
      }

      // Close modal and show success toast
      onClose();

      // Build success message
      let description = `Dispensed ${variables.medications.length} medication(s)`;
      if (printSuccessCount > 0) {
        description += ` and sent ${printSuccessCount} print request(s)`;
      } else if (printFailCount > 0) {
        description += `, but print requests failed`;
      }

      showSuccess("Medications dispensed successfully", description);

      // Show warnings separately if present
      if (response.invoiceError) {
        showWarning("Invoice Issue", response.invoiceError);
      } else if (response.invoiceWarning) {
        showWarning("Invoice Warning", response.invoiceWarning);
      }

      // Show print failure details if needed
      if (printFailCount > 0) {
        showWarning("Print Issue", "Please print labels manually");
      }
    },
    onError: (error) => {
      showError(
        "Failed to dispense medications",
        error.message || "Please try again"
      );
    },
  });

  // Helper function to calculate total dispensed and refills remaining
  const getDispenseInfo = (medication) => {
    const history = medication.dispenseHistory || [];
    // Each dispense event quantity represents packs dispensed
    const totalPacksDispensed = history.reduce(
      (sum, event) => sum + (event.quantity || 0),
      0
    );
    const refillsAllowed = medication.refillsAllowed || 1; // Default to 1 (first collection)

    // Each dispense event = 1 fill (regardless of quantity dispensed)
    const fillsUsed = history.length;

    // fillsRemaining = refillsAllowed - fillsUsed
    const fillsRemaining = Math.max(0, refillsAllowed - fillsUsed);

    return {
      history,
      totalPacksDispensed,
      fillsRemaining,
      canDispense: fillsRemaining > 0,
    };
  };

  // Show ALL medications (both dispensed and not dispensed)
  const inPracticeMedications =
    prescription.medications?.filter(
      (m) => m.dispenseLocation === "in_practice"
    ) || [];

  const toggleMedicationSelection = (medicationId) => {
    setSelectedMedications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(medicationId)) {
        newSet.delete(medicationId);
      } else {
        newSet.add(medicationId);
      }
      return newSet;
    });
  };

  const handleDispense = async () => {
    if (selectedMedications.size === 0) {
      showWarning("Please select at least one medication to dispense");
      return;
    }

    // Check if Xero is connected
    const isXeroConnected = xeroStatus?.connected || false;

    // If Xero is not connected, ask for confirmation
    // Keep window.confirm for this destructive/critical decision
    if (!isXeroConnected) {
      const confirmed = window.confirm(
        "⚠️ Xero is not connected\n\n" +
          "An invoice will NOT be created automatically for this dispense.\n" +
          "You will need to create the invoice manually later.\n\n" +
          "Do you want to proceed with dispensing?"
      );

      if (!confirmed) {
        return; // User cancelled
      }
    }

    // Build medications array - dispense the prescribed quantity
    const medicationsToDispense = Array.from(selectedMedications).map(
      (medicationId) => {
        const medication = inPracticeMedications.find(
          (m) => m.id === medicationId
        );
        return {
          medicationId,
          quantity: medication?.quantity || 1, // Use prescribed quantity
        };
      }
    );

    try {
      await dispenseMedications.mutateAsync({
        medications: medicationsToDispense,
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const getStockInfo = (medicineId) => {
    const items = inventoryItems.filter(
      (item) => item.medicineId === medicineId
    );

    // Use the statuses array from backend which is calculated dynamically
    // or fall back to checking expiryDate directly
    const nonExpiredItems = items.filter((item) => {
      // Backend returns statuses array with 'expired' if item is expired
      if (item.statuses && Array.isArray(item.statuses)) {
        return !item.statuses.includes("expired");
      }
      // Fallback: check expiryDate directly
      const expiryDate = new Date(item.expiryDate);
      return expiryDate >= new Date();
    });

    const expiredItems = items.filter((item) => {
      if (item.statuses && Array.isArray(item.statuses)) {
        return item.statuses.includes("expired");
      }
      const expiryDate = new Date(item.expiryDate);
      return expiryDate < new Date();
    });

    const availableStock = nonExpiredItems.reduce(
      (sum, item) => sum + item.currentStock,
      0
    );
    const expiredStock = expiredItems.reduce(
      (sum, item) => sum + item.currentStock,
      0
    );
    const totalStock = availableStock + expiredStock;

    // Determine status: all expired, some expired, or none expired
    const allExpired = totalStock > 0 && availableStock === 0;
    const someExpired = expiredStock > 0 && availableStock > 0;

    return {
      availableStock,
      expiredStock,
      totalStock,
      allExpired,
      someExpired,
    };
  };

  if (inPracticeMedications.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              No In-Practice Medications
            </h2>
            <p className="text-slate-600 mb-4">
              All medications in this prescription are marked for external
              pharmacy.
            </p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Dispense Medications
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Prescription #{prescription.id?.substring(0, 8)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Xero Connection Warning */}
          {xeroStatus && !xeroStatus.connected && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-yellow-900 mb-1">
                  Xero Not Connected
                </h4>
                <p className="text-sm text-yellow-800">
                  Invoices will NOT be created automatically when you dispense
                  medications. You will need to create invoices manually after
                  dispensing.
                </p>
              </div>
            </div>
          )}

          {inPracticeMedications.map((medication) => {
            const stockInfo = getStockInfo(medication.medicineId);
            const dispenseInfo = getDispenseInfo(medication);

            return (
              <div
                key={medication.id}
                className="border border-slate-200 rounded-lg p-5 space-y-4"
              >
                {/* Medication Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Package className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">
                        {medication.medicationName}
                      </h3>
                      <div className="grid grid-cols-2 gap-x-4 mt-2">
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Dosage:</span>{" "}
                          {medication.dosage}
                        </p>
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Frequency:</span>{" "}
                          {medication.frequency}
                        </p>
                        {medication.quantity && (
                          <p className="text-sm text-slate-600">
                            <span className="font-medium">Packs per fill:</span>{" "}
                            {medication.quantity}
                          </p>
                        )}
                        {medication.refillsAllowed !== undefined && (
                          <p className="text-sm text-slate-600">
                            <span className="font-medium">Total fills:</span>{" "}
                            {medication.refillsAllowed}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stock Status */}
                  <div className="text-right ml-4">
                    {isLoadingInventory ? (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-50 border border-slate-200 rounded-md">
                        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                        <span className="text-sm font-medium text-slate-600">
                          Checking stock...
                        </span>
                      </div>
                    ) : stockInfo.allExpired ? (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-200 rounded-md">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-medium text-orange-800">
                          Expired ({stockInfo.expiredStock} in stock)
                        </span>
                      </div>
                    ) : stockInfo.availableStock > 0 ? (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-200 rounded-md">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          {stockInfo.availableStock} in stock
                        </span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-md">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-800">
                          Out of stock
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dispense History */}
                {dispenseInfo.history.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-700" />
                      <h4 className="text-sm font-semibold text-blue-900">
                        Dispense History
                      </h4>
                    </div>
                    <div className="space-y-1">
                      {dispenseInfo.history.map((event, idx) => (
                        <p key={idx} className="text-xs text-blue-800">
                          <span className="font-medium">
                            Fill {idx + 1}:
                          </span>{" "}
                          {formatDate(event.dispensedAt)} - {event.quantity} pack
                          {event.quantity !== 1 ? "s" : ""}
                          {event.batchNumber &&
                            ` (Batch: ${event.batchNumber})`}
                        </p>
                      ))}
                    </div>
                    <p className="text-sm font-semibold text-blue-900 mt-2">
                      {dispenseInfo.history.length} of {medication.refillsAllowed || 1} fills
                      {" "}({dispenseInfo.totalPacksDispensed} pack
                      {dispenseInfo.totalPacksDispensed !== 1 ? "s" : ""} dispensed)
                    </p>
                  </div>
                )}

                {/* Fills Remaining */}
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-md ${
                    dispenseInfo.fillsRemaining > 0
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <RefreshCw
                    className={`w-4 h-4 ${
                      dispenseInfo.fillsRemaining > 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  />
                  <span
                    className={`text-sm font-medium ${
                      dispenseInfo.fillsRemaining > 0
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {dispenseInfo.fillsRemaining > 0
                      ? `${dispenseInfo.fillsRemaining} fill${dispenseInfo.fillsRemaining !== 1 ? "s" : ""} remaining`
                      : "No fills remaining"}
                  </span>
                </div>

                {/* Dispense Controls */}
                {stockInfo.availableStock > 0 && dispenseInfo.canDispense && (
                  <div className="pt-2 border-t flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-700">
                        <span className="font-medium">Will dispense:</span>{" "}
                        {medication.quantity || 1} pack
                        {(medication.quantity || 1) !== 1 ? "s" : ""}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Available stock: {stockInfo.availableStock} pack
                        {stockInfo.availableStock !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <Button
                      onClick={() => toggleMedicationSelection(medication.id)}
                      variant={
                        selectedMedications.has(medication.id)
                          ? "default"
                          : "outline"
                      }
                      className={
                        selectedMedications.has(medication.id)
                          ? "bg-green-600 hover:bg-green-700"
                          : ""
                      }
                    >
                      {selectedMedications.has(medication.id)
                        ? "Selected ✓"
                        : "Select to Dispense"}
                    </Button>
                  </div>
                )}

                {!dispenseInfo.canDispense && stockInfo.availableStock > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                      All allowed refills have been dispensed for this
                      medication.
                    </p>
                  </div>
                )}

                {stockInfo.allExpired && (
                  <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                    <p className="text-sm text-orange-800">
                      All stock for this medication has expired. Please order
                      new stock or refer the patient to an external pharmacy.
                    </p>
                  </div>
                )}

                {stockInfo.availableStock === 0 && !stockInfo.allExpired && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                      This medication is currently out of stock. You may need to
                      order more or refer the patient to an external pharmacy.
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Summary */}
          {selectedMedications.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">
                Dispense Summary
              </h3>
              <div className="space-y-1">
                {Array.from(selectedMedications).map((medicationId) => {
                  const med = inPracticeMedications.find(
                    (m) => m.id === medicationId
                  );
                  const qty = med?.quantity || 1;
                  return (
                    <p key={medicationId} className="text-sm text-blue-800">
                      <span className="font-medium">
                        {med?.medicationName}:
                      </span>{" "}
                      {qty} pack{qty !== 1 ? "s" : ""}
                    </p>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={handleDispense}
            disabled={
              dispenseMedications.isPending || selectedMedications.size === 0
            }
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            {dispenseMedications.isPending
              ? "Dispensing..."
              : `Dispense ${selectedMedications.size} Medication${selectedMedications.size !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
